import { inject, Injectable, signal } from '@angular/core';
import config from '../../../../../config/config';
import { HttpClient } from '@angular/common/http';
import { AuthModel, AuthModelV2, DataAuth, localStorageUser, ContextoEmpresa } from '../model/auth';
import { Router } from '@angular/router';
import { Subject, takeUntil, catchError } from 'rxjs';
import Swal from 'sweetalert2';
import { EditPermiso, Permiso, RespuestaPermisos } from '../model/permisos';
import { Permisosv1Service } from './permisosv1.service';
import { LocalstorageService, secureStorage } from './localstorage.service';
import { SweetAlertService } from '../../../shared/data-access/services/swal-alert.service';
import { NotificacionesService } from '../../../shared/data-access/services/notificaciones.service';


@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  //int_cliente_id logueado
  private int_usuario_sistema_centro_id: number = 0;
  private int_usuario_id: number = 0;
  private str_rol_nombre: string = '';
  public int_cliente_id: number = 0;
  public permisos: Permiso[] = [];
  public int_sistema_id: number = 1;

  private readonly destroy$ = new Subject<any>();

  // === SIGNALS ===
  readonly permisosModulos = signal<Permiso[]>([]);
  readonly contextos = signal<ContextoEmpresa[]>([]);
  readonly requiereSeleccionPerfil = signal<boolean>(false);
  readonly requiereSeleccion = signal<boolean>(false);
  readonly requiereContrasena = signal<boolean>(false);
  readonly resumenDestino = signal<string>('');
  readonly currentUser = signal<localStorageUser | null>(this.obtenerUserData());
  readonly isLoggingIn = signal<boolean>(false);

  private lastAuthData: DataAuth | null = null;

  router = inject(Router);
  private readonly notificacionesService = inject(NotificacionesService);

  setIdUsuarioSistemaCentro(id: number) {
    this.int_usuario_sistema_centro_id = id;
  }
  setIdUsuario(id: number) {
    this.int_usuario_id = id;
  }

  private readonly URL_API_INICIAR_SESION =
    config.URL_API_BASE + '/login/iniciar-sesion-v2';
  private readonly URL_API_INICIAR_SESION_FALLBACK =
    config.URL_API_BASE + '/login/iniciar-sesion';
  private readonly URL_API_PERMISOS =
    config.URL_API_BASE + '/permisos/v2/byIdUser';
  private readonly URL_API_PERMISOS_FALLBACK =
    config.URL_API_BASE + '/permisos/byIdUser';
  private readonly URL_API_PERMISOS_EDIT = config.URL_API_BASE + '/permisos';

  private decodeJwtPayload(token: string): Record<string, any> | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) {
        return null;
      }

      const normalized = payload.replaceAll('-', '+').replaceAll('_', '/');
      return JSON.parse(atob(normalized));
    } catch {
      return null;
    }
  }

  constructor(private readonly srvSwal: SweetAlertService, private readonly http: HttpClient, private readonly srvPermisosv1: Permisosv1Service, private readonly formStateService: LocalstorageService) {
  }

  postIniciarSesion(data: DataAuth) {
    return this.http.post<AuthModelV2>(this.URL_API_INICIAR_SESION, data, {
      withCredentials: true
    });
  }

  postIniciarSesionFallback(data: DataAuth) {
    return this.http.post<AuthModel>(this.URL_API_INICIAR_SESION_FALLBACK, data, {
      withCredentials: true
    });
  }


  getPermisosUsuarioLogueado(id: number) {
    return this.http.get<RespuestaPermisos>(this.URL_API_PERMISOS + '/' + id);
  }

  getPermisosUsuarioLogueadoFallback(id: number) {
    return this.http.get<RespuestaPermisos>(this.URL_API_PERMISOS_FALLBACK + '/' + id);
  }

  //servicio para editar un permiso dado el id del permiso
  patchPermiso(id: number, permiso: Permiso) {
    return this.http.patch<EditPermiso>(
      this.URL_API_PERMISOS_EDIT + '/' + id,
      permiso
    );
  }

  logout() {
    this.removeUser();
    //apaga el sondeo y vacía el buzón en memoria: sin esto, el siguiente usuario que inicie sesión en
    //la misma pestaña veía por un instante las notificaciones del anterior.
    this.notificacionesService.limpiar();
    this.requiereSeleccionPerfil.set(false);
    this.requiereSeleccion.set(false);
    this.requiereContrasena.set(false);
    this.resumenDestino.set('');
    this.contextos.set([]);
    this.lastAuthData = null;
    this.router.navigateByUrl('/');
  }

  private getMensajeErrorLogin(message: string): string {
    const normalizado = message.trim().toLowerCase();

    if (normalizado === 'no se encontró el usuario') {
      return 'Correo no registrado';
    }

    if (normalizado === 'credenciales incorrectas') {
      return 'Correo o contraseña inválidos';
    }

    if (normalizado === 'el usuario no tiene contextos activos en empresas/centros') {
      return 'No tienes contextos activos asignados';
    }

    if (normalizado === 'el usuario no tiene acceso a la empresa indicada') {
      return 'No tienes acceso al contexto seleccionado';
    }

    return message;
  }

  private procesarRespuestaAutenticacion(res: AuthModelV2 | AuthModel, titulo: string) {
    if (!res.status) {
      Swal.close();
      this.srvSwal.fire({
        icon: 'error',
        title: 'Error',
        text: this.getMensajeErrorLogin(res.message),
      });
      return;
    }

    if (this.manejarSeleccionesRequeridas(res)) {
      return;
    }

    this.finalizarSesionExitosa(res, titulo);
  }

  private ejecutarAutenticacion(data: DataAuth, tituloExito: string, tituloCarga: string) {
    this.srvSwal.fire({
      title: tituloCarga,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    this.postIniciarSesion(data)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('V2 login failed, attempting fallback to V1', error);
          return this.postIniciarSesionFallback(data);
        })
      )
      .subscribe({
        next: (res: AuthModelV2 | AuthModel) => {
          this.procesarRespuestaAutenticacion(res, tituloExito);
        },
        error: (error) => {
          Swal.close();
          this.srvSwal.fire({
            icon: 'error',
            title: 'Error de Autenticación',
            text: 'No se pudo procesar tu solicitud',
          });
          console.error('Login error:', error);
        }
      });
  }

  private manejarSeleccionesRequeridas(res: AuthModelV2 | AuthModel): boolean {
    if (typeof res.body !== 'object') {
      return false;
    }

    if (
      ('requiereSeleccionContexto' in res.body && res.body.requiereSeleccionContexto) ||
      ('requiereSeleccionEmpresa' in res.body && res.body.requiereSeleccionEmpresa)
    ) {
      Swal.close();
      this.requiereSeleccionPerfil.set(false);
      const contextos = res.body.contextos || [];
      this.contextos.set(contextos);
      this.requiereContrasena.set(false);

      if ('preauth_token' in res.body && res.body.preauth_token) {
        this.lastAuthData = {
          ...(this.lastAuthData || { email: '' }),
          preauth_token: res.body.preauth_token,
        };
      }

      if (contextos.length === 1) {
        this.requiereSeleccion.set(false);
        this.seleccionarEmpresaCentro(contextos[0]);
        return true;
      }

      if (contextos.length > 1) {
        this.requiereSeleccion.set(true);
        return true;
      }

      this.requiereSeleccion.set(false);
      if (contextos.length === 0) {
        this.srvSwal.fire({
          title: 'Sin contextos activos',
          html: '<p>No tienes contextos activos asignados</p>',
          icon: 'info',
        });
      }
      return true;
    }

    return false;
  }

  private finalizarSesionExitosa(res: AuthModelV2 | AuthModel, titulo: string) {
    console.log(res)
    let idSistema: number = 0;
    let esSuperAdmin = false;

    const token = typeof res.body === 'string' ? res.body : res.body?.token;
    if (token) {
      secureStorage.setItem('tokenP', token);
      const decodedToken = this.decodeJwtPayload(token);
      esSuperAdmin = Boolean(decodedToken?.['es_super_admin']);
    }

    Swal.close();
    this.srvSwal.fire({
      icon: 'success',
      title: titulo,
      text: res.message,
    });

    if (res.cliente) {
      this.int_cliente_id = res.cliente.int_cliente_id;
      this.formStateService.guardarCliente(res.cliente);
    }

    if (res.usuario?.int_usuario_sistema_centro_id) {
      idSistema = res.usuario.int_usuario_sistema_centro_id;
      this.str_rol_nombre = res.cargo || '';
      this.saveRol(this.str_rol_nombre);
      this.setIdUsuarioSistemaCentro(idSistema);
      this.obtenerPermisosUsuarioLogueado(idSistema);
    } else if (res.usuario?.usuario) {
      idSistema = res.usuario.usuario.int_usuario_id;
      this.setIdUsuario(idSistema);
    }

    const usuarioData = res.usuario?.usuario || (res.usuario as any);
    console.log(usuarioData)
    this.saveUser({
      usuario: usuarioData,
      sistema_id: idSistema,
      es_super_admin: esSuperAdmin,
    });

    this.currentUser.set({
      usuario: usuarioData,
      sistema_id: idSistema,
      es_super_admin: esSuperAdmin,
    });

    this.requiereSeleccionPerfil.set(false);
    this.requiereSeleccion.set(false);
    this.requiereContrasena.set(false);
    this.contextos.set([]);

    setTimeout(() => {
      this.router.navigateByUrl('/empresas');
    }, 1000);
  }

  private saveUser(user: Object) {
    secureStorage.setItem('userData', JSON.stringify(user));
  }
  private saveRol(rol: string) {
    secureStorage.setItem('cargo', rol);
  }

  public updateUser(user: Object) {
    //se combina con la sesión actual: la respuesta del perfil no trae
    //int_usuario_sistema_centro_id ni es_super_admin (solo vienen del login)
    //y sin ellos el guard de permisos consulta con id 0 y deniega el acceso.
    const actual: localStorageUser = secureStorage.getItem('userData') ?? {};
    const data = {
      ...actual,
      usuario: { ...actual.usuario, ...user },
    } as localStorageUser;
    this.saveUser(data);
    this.currentUser.set(data);
  }

  // private removeToken() {
  //   secureStorage.removeItem('userData');
  // }

  private removeUser() {
    secureStorage.removeItem('userData');
    secureStorage.removeItem('empresa');
    secureStorage.removeItem('centro');
    secureStorage.removeItem('resumen');
    secureStorage.removeItem('selectedEmpresaId');
    secureStorage.removeItem('cargo');
    secureStorage.removeItem('selectedAjustesCentro');
    secureStorage.removeItem('selectedAjustesEmpresa');
    secureStorage.removeItem('selectedAjustesUsuarioId');
    secureStorage.removeItem('clienteISO');
    secureStorage.removeItem('participante_cursos');
    secureStorage.removeItem('token_cursos');
    this.srvPermisosv1.clearPermisos();
    this.formStateService.clearAllFormStates();
  }

  private obtenerUserData() {
    return secureStorage.getItem('userData');
  }

  iniciarSesion(data: DataAuth) {
    this.lastAuthData = data;
    this.resumenDestino.set('');
    this.ejecutarAutenticacion(data, 'Bienvenido', 'Iniciando sesión ...');
  }

  // Método para manejar selección de empresa/centro por parte del usuario
  seleccionarEmpresaCentro(contexto: ContextoEmpresa) {
    if (!this.lastAuthData) {
      this.srvSwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Datos de sesión perdidos. Intenta nuevamente.',
      });
      return;
    }

    // Reenviar login con los datos seleccionados
    const dataConContexto: DataAuth = {
      email: this.lastAuthData.email,
      int_empresa_id: contexto.int_empresa_id,
      int_centro_id: contexto.int_centro_id,
    };

    if (this.lastAuthData.preauth_token) {
      dataConContexto.preauth_token = this.lastAuthData.preauth_token;
    }

    this.lastAuthData = dataConContexto;
    const centroTexto = contexto.str_centro_nombre ? ` | Centro: ${contexto.str_centro_nombre}` : '';
    this.resumenDestino.set(`Empresa: ${contexto.str_empresa_nombre}${centroTexto}`);

    this.srvSwal.fire({
      title: 'Completando Autenticación ...',
      didOpen: () => {
        Swal.showLoading();
      },
    });

    this.postIniciarSesion(dataConContexto)
      .pipe(
        takeUntil(this.destroy$),
        catchError((error) => {
          console.warn('V2 login with context failed', error);
          return this.postIniciarSesionFallback(dataConContexto);
        })
      )
      .subscribe({
        next: (res: AuthModelV2 | AuthModel) => {
          this.procesarRespuestaAutenticacion(res, 'Autenticado');
        },
        error: (error) => {
          Swal.close();
          this.srvSwal.fire({
            icon: 'error',
            title: 'Error',
            text: 'No se pudo completar la autenticación',
          });
          console.error('Context selection error:', error);
        }
      });
  }

  //funcion para obtener los permisos del usuario logueado
  // Por defecto fuerza una carga nueva (login, edición de permisos). Con forzar=false
  // reutiliza la carga en curso, p. ej. si el guard ya la inició al recargar la página.
  // La petición la comparte Permisosv1Service con el guard, que espera esta misma respuesta.
  obtenerPermisosUsuarioLogueado(id: number, forzar = true) {
    this.srvPermisosv1
      .cargar$(id, forzar)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (permisos: Permiso[]) => this.permisosModulos.set(permisos),
        error: (error) => console.error('No se pudieron obtener los permisos', error),
      });
  }
  //funcion que obtiene los permisos del usuario logueado devuelve un observable
  obtenerPermisosUsuarioLogueado$(
    id: number
  ) {
    return this.getPermisosUsuarioLogueado(id).pipe(
      takeUntil(this.destroy$)
    );
  }

  //funcion para editar un permiso dado el id del permiso
  editarPermiso(id: number, permiso: Permiso) {
    this.patchPermiso(id, permiso)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res: EditPermiso) => {
          if (res.status) {
            //obtenemos los permisos del usuario
            const id = secureStorage.getItem('userData');
            if (id) {
              this.obtenerPermisosUsuarioLogueado(id.sistema_id);
            }
          }
        },
      });
  }


}
